import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store/root-reducer';

import { buildBalanceSeries, sortEventsDesc } from '../domain/credit-ledger.rules';
import type { PrimaryContact } from '../domain/licence-detail.model';
import {
  addContactBlocker,
  invitationBacklog,
  primaryContactCandidates,
} from '../domain/licence-detail.rules';
import { licenceGlance } from '../domain/licence-overview.rules';

const selectLicenceDetail = (state: RootState) => state.licenceDetail;

export const selectLicence = (state: RootState) => selectLicenceDetail(state).licence;
export const selectUsers = (state: RootState) => selectLicenceDetail(state).users;
export const selectLists = (state: RootState) => selectLicenceDetail(state).lists;
export const selectLoadStatus = (state: RootState) => selectLicenceDetail(state).loadStatus;
export const selectNotify = (state: RootState) => selectLicenceDetail(state).notify;
export const selectUserDelete = (state: RootState) => selectLicenceDetail(state).userDelete;
export const selectUserArchive = (state: RootState) => selectLicenceDetail(state).userArchive;
export const selectEdit = (state: RootState) => selectLicenceDetail(state).edit;
/** Lifecycle of the Danger zone's deactivate/reactivate (#582). */
export const selectLicenceStatusMutation = (state: RootState) =>
  selectLicenceDetail(state).licenceStatus;
/** Lifecycle of the Danger zone's deletion (#584). */
export const selectLicenceDeleteMutation = (state: RootState) =>
  selectLicenceDetail(state).licenceDelete;
export const selectContactsSave = (state: RootState) => selectLicenceDetail(state).contactsSave;
export const selectListMutation = (state: RootState) => selectLicenceDetail(state).listMutation;
export const selectApprovers = (state: RootState) => selectLicenceDetail(state).approvers;
export const selectCsmCandidates = (state: RootState) => selectLicenceDetail(state).csmCandidates;
export const selectClientCategories = (state: RootState) =>
  selectLicenceDetail(state).clientCategories;

const NO_CONTACTS: PrimaryContact[] = [];

/** The licence's main contacts (#564); a stable empty array before the licence loads. */
export const selectPrimaryContacts = (state: RootState) =>
  selectLicenceDetail(state).licence?.primary_contacts ?? NO_CONTACTS;

/** Users who may be added as a main contact; memoised on users + contacts. */
export const selectContactCandidates = createSelector(
  selectUsers,
  selectPrimaryContacts,
  primaryContactCandidates,
);

/** Why "Add contact" is unavailable, or null; memoised. */
export const selectAddContactBlocker = createSelector(
  selectPrimaryContacts,
  selectContactCandidates,
  addContactBlocker,
);

/** How many users still have no access; memoised, drives the "Send Invitations" CTA. */
/** Who still cannot sign in, for the Users tab's banner (#574). */
export const selectInvitationBacklog = createSelector(selectUsers, invitationBacklog);

/** The identity panel's "At a glance" figures (#565, #750); memoised on users, lists and licence. */
export const selectGlance = createSelector(selectUsers, selectLists, selectLicence, licenceGlance);

// ── Activity feed (#566) ──
export const selectActivity = (state: RootState) => selectLicenceDetail(state).activity;

// ── Contract history (#750) ──
export const selectContractHistory = (state: RootState) => selectLicenceDetail(state).contractHistory;

// ── Notes (#605) ──
export const selectNotes = (state: RootState) => selectLicenceDetail(state).notes;

// ── Credits ──
export const selectCreditBalance = (state: RootState) => selectLicenceDetail(state).credits.balance;
export const selectCreditEvents = (state: RootState) => selectLicenceDetail(state).credits.events;
export const selectGrant = (state: RootState) => selectLicenceDetail(state).grant;
export const selectCreditsError = (state: RootState) => selectLicenceDetail(state).credits.error;

/** True only on the very first load, so a post-grant refetch keeps the tab rendered. */
export const selectCreditsLoading = (state: RootState) => {
  const { loadStatus, events } = selectLicenceDetail(state).credits;
  return loadStatus === 'loading' && events.length === 0;
};

/** Cumulative balance per movement; memoised on the events reference. */
export const selectBalanceSeries = createSelector(selectCreditEvents, buildBalanceSeries);

/** Movements most-recent-first for the table; memoised on the events reference. */
export const selectCreditEventsDesc = createSelector(selectCreditEvents, sortEventsDesc);
